import React from 'react';

import { QueryResult } from '@apollo/client/react';
import { Box, Icon, Link, Shimmer, Text, ToggleTip } from '@betterment/samba-react';

import { EstimatedTradeTimingQuery } from './graphql/query.estimatedTradeTiming.generated';
import { SaleDetailsRow } from './SaleDetailsRow';

import urlBuilder from '../../../Shared/url-builder';

type TradeTimingRowProps = {
  tradeTimingResult: Pick<QueryResult<EstimatedTradeTimingQuery>, 'data' | 'error'>;
};

const TradeTimingLabelAndValue: React.FC<React.PropsWithChildren> = ({ children }) => (
  <SaleDetailsRow>
    <SaleDetailsRow.Label>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.50' }}>
        <Text>Trade timing</Text>
        <ToggleTip>
          <ToggleTip.Trigger label="Trade timing information" />
          <ToggleTip.Content align="start" side="bottom" css={{ maxWidth: '300px' }}>
            <Text as="p">
              Your order will be placed in the next trading window at the market price available when it executes.{' '}
              <Link href={urlBuilder.getSelfDirectedInvestingDisclosure()} target="_blank">
                See disclosure
              </Link>
            </Text>
            <Text as="p" css={{ mt: 'space.100' }}>
              Mutual fund trades execute only once per day at market close.
            </Text>
            <Text as="p" css={{ mt: 'space.100' }}>
              Order values may fluctuate before execution due to market volatility.
            </Text>
          </ToggleTip.Content>
        </ToggleTip>
      </Box>
    </SaleDetailsRow.Label>
    <SaleDetailsRow.Value data-testid="trade-timing-value">{children}</SaleDetailsRow.Value>
  </SaleDetailsRow>
);

export const TradeTimingRow: React.FC<TradeTimingRowProps> = ({ tradeTimingResult }) => {
  const { error, data } = tradeTimingResult;

  if (error) {
    return <TradeTimingLabelAndValue>—</TradeTimingLabelAndValue>;
  }

  if (!data) {
    return (
      <TradeTimingLabelAndValue>
        <Shimmer data-testid="trade-timing-shimmer" css={{ height: 'size.300', width: '240px' }} />
      </TradeTimingLabelAndValue>
    );
  }

  const tradeTiming = data.estimatedTradeTiming;

  if (tradeTiming.__typename === 'EstimatedTradeTimeRange') {
    const display = `${tradeTiming.dayDescription}, ${tradeTiming.timeRangeDescription}`;
    return <TradeTimingLabelAndValue>{display}</TradeTimingLabelAndValue>;
  }

  if (tradeTiming.__typename === 'EstimatedTradeDate') {
    return (
      <SaleDetailsRow
        css={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'space.100',
        }}
        data-testid="trade-timing-value"
      >
        <Icon name="InfoFilled" size="xsmall" color="icon.weak" css={{ mt: 'space.25', flexShrink: 0 }} />
        <Text variant="text100">{tradeTiming.description}</Text>
      </SaleDetailsRow>
    );
  }

  throw new Error('Unknown trade timing type');
};
